import React, { useState, useEffect } from "react"
import { Input } from "reactstrap"
import moment from "moment"
import loadingAnim from "common/animations/loading-dots-blue"
import findAnim from "common/animations/finddata"
import nodataAnim from "common/animations/nodata"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import "moment/locale/fi"
import "moment/locale/se"
import "moment/locale/en-gb"
import fin from "antd/es/locale/fi_FI"
import swe from "antd/es/locale/sv_SE"
import en from "antd/es/locale/en_GB"
import dayjs from "dayjs"

// Excel export
import XLSX from "xlsx"
// Autotable PDF
import jsPDF from "jspdf"
import "jspdf-autotable"

// Default Antd table page size options
export const defaultPageSizeOptions = [10, 30, 50, 100, 200, 500]

// Separators of terms in search fields
export const orSeparator = " || "

/**
 * Check if given string is a valid UUID
 * @param {*} str
 * @returns boolean
 */
export function checkIfValidUUID(str) {
  const regexExp =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi
  return regexExp.test(str)
}

// Get random number
export function randomNumber() {
  const crypto = globalThis.crypto || globalThis.msCrypto
  let array = new Uint32Array(1)
  crypto.getRandomValues(array)
  return array[0]
}

function dec2hex(dec) {
  return dec.toString(16).padStart(2, "0")
}

// Generate password
export function generatePassword(len) {
  const crypto = globalThis.crypto || globalThis.msCrypto
  let array = new Uint8Array((len || 40) / 2)
  crypto.getRandomValues(array)
  return Array.from(array, dec2hex).join("")
}

// Generate strong password
export function generateStrongPassword() {
  const crypto = globalThis.crypto || globalThis.msCrypto

  function shuffle(array) {
    let currentIndex = array.length
    while (currentIndex != 0) {
      let randomIndex = Math.floor(Math.random() * currentIndex)
      currentIndex--
      ;[array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ]
    }
  }

  let upper = "ABCDEFGHJKLMNPQRSTUVWXYZ"
  let lower = "abcdefghijkmnopqrstuvwxyz"
  let number = "0123456789"
  let special = "!?#@%&*"

  let temp =
    Array.from(crypto.getRandomValues(new Uint32Array(4)))
      .map(x => upper[x % upper.length])
      .join("") +
    Array.from(crypto.getRandomValues(new Uint32Array(5)))
      .map(x => lower[x % lower.length])
      .join("") +
    Array.from(crypto.getRandomValues(new Uint32Array(4)))
      .map(x => number[x % number.length])
      .join("") +
    Array.from(crypto.getRandomValues(new Uint32Array(1)))
      .map(x => special[x % special.length])
      .join("")

  let arr = temp.split("")
  shuffle(arr)
  return arr.join("")
}

/**
 * @param {String} dateString
 * @returns Formatted date as "DD.MM.YYYY HH:mm:ss"
 */
export function formatDateFull(date) {
  return moment(date).format("D.M.YYYY HH:mm:ss")
}
export function formatDateHoursMinutes(date) {
  return moment(date).format("D.M.YYYY HH:mm")
}
export function formatDateStringFull(dateString) {
  return moment(dateString).format("DD.MM.YYYY HH:mm:ss")
}
export function formatDate(date) {
  if (!date) return ""
  return moment(date).format("DD.MM.YYYY")
}
export function formatDateSimple(date) {
  if (!date) return ""
  return moment(date).format("D.M.YYYY")
}
export function formatDateWithWeekDay(date) {
  return moment(date).format("dddd")
}
export function dateFormatter(value) {
  if (!value) return ""
  return moment(value).format("DD.MM.YYYY HH:mm:ss")
}
export function formatDateTime(date) {
  return dayjs(date).format("HH:mm")
}
export function boolFormatterX(cell) {
  if (cell) {
    return "x"
  } else return ""
}
export function checkboxFormatter(cell, row) {
  if (row.linked) {
    return <Input type="checkbox" disabled defaultChecked={true}></Input>
  } else return <></>
}

// Time formatter
export function timeFormatter(value) {
  let hours = Math.floor(value / 3600) // get hours
  let minutes = Math.floor((value - hours * 3600) / 60) // get minutes
  let seconds = value - hours * 3600 - minutes * 60 //  get seconds
  if (hours < 10) {
    hours = "0" + hours
  }
  if (minutes < 10) {
    minutes = "0" + minutes
  }
  if (seconds < 10) {
    seconds = "0" + seconds
  }
  return value ? hours + ":" + minutes + ":" + seconds : ""
}

export function getMedian(values) {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

export function contentImageFormatter(cell) {
  if (cell) {
    return (
      <img
        className="rounded bg-light"
        style={{ objectFit: "cover", width: "60px", height: "60px" }}
        src={"data:image/jpeg;base64," + cell}
        alt=""
      ></img>
    )
  } else return null
}

// Sort strings
export function stringSorter(a, b, param, sortOrder = "ascend") {
  if (a[param] && b[param]) {
    return a[param].localeCompare(b[param])
  } else if (a[param]) {
    return sortOrder === "ascend" ? -1 : 1
  } else if (b[param]) {
    return sortOrder === "ascend" ? 1 : -1
  }
  return 0
}

// Sort numbers - nulls always last
export function numberSorter(a, b, sortOrder = "ascend") {
  if (a === "undefined") a = "0"
  if (b === "undefined") b = "0"

  if (a != null && b != null) {
    return a - b
  } else if (a != null) {
    return sortOrder === "ascend" ? -1 : 1
  } else if (b != null) {
    return sortOrder === "ascend" ? 1 : -1
  }
  return 0
}

export function numStringSorter(a, b) {
  if (a != null && b != null) {
    const typeA = typeof a
    const typeB = typeof b
    if (typeA === "number" && typeB === "number") return a - b
    if (typeA === "string" && typeB === "string") return b.localeCompare(a)
    // numbers come before strings
    return typeA === "number" ? -1 : 1
  } else if (a != null) {
    return -1
  } else if (b != null) {
    return 1
  }
  return 0
}

// Sort booleans - nulls always last
export function booleanSorter(a, b, param, sortOrder = "ascend") {
  if (a[param] != null && b[param] != null) {
    return b[param] - a[param]
  } else if (a[param] != null) {
    return sortOrder === "ascend" ? -1 : 1
  } else if (b[param] != null) {
    return sortOrder === "ascend" ? 1 : -1
  }
  return 0
}

// Simple number sorter antd
export function numSorter(a, b) {
  if (a && b) {
    return a - b
  }
  return 0
}

// Sort dates - nulls and zeros always last
export function dateSorter(a, b, sortOrder = "ascend") {
  if (a && b) {
    return a - b
  } else if (a) {
    return sortOrder === "ascend" ? -1 : 1
  } else if (b) {
    return sortOrder === "ascend" ? 1 : -1
  }
  return 0
}

// Sort dates - nulls and zeros always last
export function dateSorterDateObj(a, b, sortOrder = "ascend") {
  if (a && b) {
    return new Date(a) - new Date(b)
  } else if (a) {
    return sortOrder === "ascend" ? -1 : 1
  } else if (b) {
    return sortOrder === "ascend" ? 1 : -1
  }
  return 0
}

/**
 * @returns Options for Lottie
 */
export function getLottieLoadingOptions() {
  return {
    loop: true,
    autoplay: true,
    animationData: loadingAnim,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  }
}

export function getLottieSearch() {
  return {
    loop: false,
    autoplay: true,
    animationData: findAnim,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  }
}

export function getLottieNothingFound() {
  return {
    loop: false,
    autoplay: true,
    animationData: nodataAnim,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  }
}

// Show toast
export function showToast(
  message,
  type = "info",
  timeOut = 8000,
  position = "toast-top-center"
) {
  toastr.options = {
    positionClass: position,
    timeOut: timeOut,
    extendedTimeOut: 1000,
    closeButton: true,
    progressBar: true,
    showEasing: "swing",
    hideEasing: "linear",
    showMethod: "fadeIn",
    hideMethod: "fadeOut",
    showDuration: 300,
    hideDuration: 1000,
  }
  switch (type) {
    case "success":
      toastr.success(message)
      break
    case "error":
      toastr.error(message)
      break
    case "warning":
      toastr.warning(message)
      break
    default:
      toastr.info(message)
      break
  }
}

// Get locale for antd datepicker etc.
export function getLocale() {
  let language = localStorage.getItem("i18nextLng")
  if (language.includes("-")) language = language.split("-")[0]
  switch (language) {
    case "en":
      return en
    case "fi":
      return fin
    case "sv":
      return swe
    default:
      return null
  }
}

// Get locale text: en / fi / sv
export function getLocaleText() {
  let language = localStorage.getItem("i18nextLng")
  if (language.includes("-")) language = language.split("-")[0]
  switch (language) {
    case "en":
      return "en"
    case "fi":
      return "fi"
    case "sv":
      return "sv"
    default:
      return undefined
  }
}

// Formatting bool for export files
export function isTrue(value) {
  if (value === true) {
    return "X"
  } else {
    return ""
  }
}

// Download excel
export function downloadExcel(title, heading, data) {
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(data, {
    origin: "A2",
    skipHeader: true,
  })
  XLSX.utils.sheet_add_aoa(ws, heading, { origin: "A1" })
  XLSX.utils.book_append_sheet(wb, ws, title)
  XLSX.write(wb, { bookType: "xlsx", type: "buffer" })
  XLSX.writeFile(wb, title + " (" + formatDate(new Date()) + ")" + ".xlsx")
}

// Export PDF table
export function exportPDF(title, heading, data, date, styles) {
  const unit = "pt"
  const size = "A4" // Use A1, A2, A3 or A4
  const orientation = "landscape" // portrait or landscape
  const theme = "grid" //striped,  plain

  const marginLeft = 40
  const doc = new jsPDF(orientation, unit, size, theme)

  doc.setFontSize(14)

  let content = {
    startY: 50,
    head: [heading],
    body: data,
    columnStyles: styles,
  }

  doc.text(title, marginLeft, 40)
  doc.autoTable(content)
  if (date) {
    doc.save(title + " (" + formatDate(new Date()) + ")" + ".pdf")
  } else {
    doc.save(title)
  }
}

// Delete duplicates from array
export function deleteDuplicated(array) {
  let newArray = array.filter(function (elem, pos) {
    return array.indexOf(elem) == pos
  })
  //console.log("new set:" + newArray)
  return newArray
}

// Delete duplicates from array of objects
export function deleteDuplicatedObject(arr) {
  let newArr = arr.filter(
    (v, i, a) => a.findIndex(v2 => v2.value === v.value) === i
  )
  return newArr
}

// Get translatable role name
export function getTranslatedRole(role) {
  switch (role) {
    case "system_admin":
      return "System Admin"
    case "organisation_admin":
      return "Organisation Admin"
    case "organisation_user":
      return "Organisation User"
    case "private_user":
      return "Private User"
    case "salesperson":
      return "Salesperson"
    case "order_manager":
      return "Order Manager"
    default:
      return ""
  }
}

// Set dev environment according to page URL
export const useDevEnvironment = () => {
  const [devEnvironment, setDevEnvironment] = useState({
    isTestEnvironment: false,
    isBetaEnvironment: false,
    isLocalhostEnvironment: false,
  })

  useEffect(() => {
    const url = globalThis.location.href

    setDevEnvironment({
      isTestEnvironment:
        url.includes("test.suvanto.care") || url.includes("test2.suvanto.care"),
      isBetaEnvironment: url.includes("beta.suvanto.care"),
      isLocalhostEnvironment: url.includes("localhost"),
    })
  }, [])

  return devEnvironment
}

// Battery Status
export function getBatteryStatus(batteryLevel, t) {
  if (batteryLevel < 1) {
    return (
      <div className="font-size-12 text-danger text-nowrap">
        <i className="mdi mdi-alert font-size-14 me-2"></i>
        {t("Battery empty")}
      </div>
    )
  } else if (batteryLevel < 6) {
    return (
      <div className="font-size-12 text-danger text-nowrap">
        <i className="mdi mdi-alert font-size-14 me-2"></i>
        {t("Battery critical")}
      </div>
    )
  } else if (batteryLevel < 30) {
    return (
      <div className="font-size-12 text-danger text-nowrap">
        <i className="mdi mdi-alert font-size-14 me-2"></i>
        {t("Battery low")}
      </div>
    )
  } else {
    return (
      <div className="font-size-12 text-success text-nowrap">
        <i className="mdi mdi-checkbox-marked-circle font-size-14 me-2"></i>
        {t("Battery good")}
      </div>
    )
  }
}

// Sort data by group first, then by name
export const sortData = data => {
  return data.sort((a, b) => {
    if (a.group.toLowerCase() < b.group.toLowerCase()) return -1
    if (a.group.toLowerCase() > b.group.toLowerCase()) return 1
    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1
    return 0
  })
}

// Get operator value based on iccid
export const getOperatorByIccid = iccid => {
  const iccidPrefix = iccid ? iccid.slice(0, 7) : ""
  let operator = ""

  if (iccidPrefix === "8945042") {
    operator = "Telia"
  } else if (iccidPrefix === "8935806") {
    operator = "DNA"
  } else if (iccidPrefix === "8935802") {
    operator = "Elisa"
  } else if (iccidPrefix === "8934075") {
    operator = "Telefonica"
  }
  return operator
}

// Format operator strings
export const operatorFormatter = operatorText => {
  let badgeClass = ""

  switch (operatorText) {
    case "Elisa":
      badgeClass = "bg-success"
      break
    case "Telia":
      badgeClass = "bg-tertiary"
      break
    case "DNA":
      badgeClass = "bg-primary"
      break
    default:
      badgeClass = "bg-danger"
  }

  return (
    <span className={`badge ${badgeClass} font-size-12`}>{operatorText}</span>
  )
}

// Update url by search input to ?search=
export const searchToURL = (searchText, location, navigate) => {
  const params = new URLSearchParams(location.search)

  if (searchText.trim()) {
    // If search input has values, update URL
    params.set("search", searchText.trim())
  } else if (params.has("search")) {
    // If the search is empty and `search` exists in the URL, remove it
    params.delete("search")
  }

  // Update URL only if it has changed
  navigate(`?${params.toString()}`, { replace: false })
}

export function calculateHomeCardData({
  chartDataDoor,
  chartDataMicro,
  chartDataWc,
  chartDataFridge,
  chartDataCoffee,
  setDoorTimes,
  setMicroTimes,
  setWcTimes,
  setFridgeTimes,
  setCoffeeTimes,
}) {
  // Door
  if (chartDataDoor.length) {
    const result = chartDataDoor.map(c => c[1]).reduce((a, b) => a + b, 0)
    setDoorTimes(result)
  }
  // Microwave
  if (chartDataMicro.length) {
    const result = chartDataMicro.map(c => c[1]).reduce((a, b) => a + b, 0)
    setMicroTimes(result)
  }
  // Coffee
  if (chartDataCoffee.length) {
    // Aja vain, jos dataa on
    const uses = chartDataCoffee.reduce(
      (count, [, v], i, arr) => {
        // count = kertymä, v = nykyhetken 0/1-arvo
        const prev = i > 0 ? arr[i - 1][1] : 0 // edellinen arvo; alussa oletetaan 0
        // Lisää yksi käyttö, kun alkaa uusi "1-jakso" (nykyinen on 1 ja edellinen ei ollut 1)
        return count + (v === 1 && prev !== 1 ? 1 : 0)
      },
      0 // lähtöarvo laskurille
    )
    setCoffeeTimes(uses) // talleta jaksoittaisten ykkösten (0→1 -siirtymien) lukumäärä
  }
  // WC
  if (chartDataWc.length) {
    let count = 0
    const row = chartDataWc.map(c => {
      count = c[1] > 0 ? count + 1 : 0
      return count
    })
    const result = row.filter(num => num === 1).length
    setWcTimes(result)
  }
  // Fridge
  if (chartDataFridge.length) {
    const result = chartDataFridge.map(c => c[1]).reduce((a, b) => a + b, 0)
    setFridgeTimes(result)
  }
}

export const alarmColorsMap = {
  Devices: {
    color: "#7E9CB1",
    alarms: [1, 2, 10, 11, 26, 29, 32, 33, 44, 45, 46, 47, 48, 51, 52, 53],
  },
  Apartment: {
    color: "#2b9cbe",
    alarms: [20, 21, 22, 23, 37, 38, 18, 19, 54],
  },
  Safety: {
    color: "#76d7c4",
    alarms: [3, 4, 35, 36, 42, 5],
  },
  Activity: {
    color: "#52be80",
    alarms: [6, 12, 14, 16, 24, 25],
  },
  Health: {
    color: "#f1948a",
    alarms: [30, 27, 28, 31],
  },
  "Abnormal activity": {
    color: "#f8c471",
    alarms: [17, 15, 13],
  },
  Other: {
    color: "#c39bd3",
    alarms: [34, 39, 43],
  },
}

export function getAlarmColor(alarmId) {
  for (let category in alarmColorsMap) {
    if (alarmColorsMap[category].alarms.includes(Number.parseInt(alarmId))) {
      return alarmColorsMap[category].color
    }
  }
  return "#000"
}

// Sanitize settings for bulletins
export const bulletinSanitizeSettings = {
  allowedTags: [
    "b",
    "i",
    "em",
    "strong",
    "u",
    "a",
    "ul",
    "ol",
    "li",
    "br",
    "p",
    "span",
  ],
  allowedAttributes: {
    a: ["href", "target", "rel"],
  },
  nonBooleanAttributes: [],
}
